import React, { useState, useEffect } from 'react';
import {
  Box,
  Fab,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  Restaurant,
  TrendingUp,
  Warning,
  Inventory,
  Category,
  History,
  Fastfood,
  Add as AddIcon,
  ArrowUpward,
  Store,
  LocalOffer,
  RoomService,
  TakeoutDining,
  DeliveryDining,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import InventoryLogs from './InventoryLogs';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Styled Components
const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const StockIndicator = styled(Box)(({ theme, stockLevel }) => {
  const getColor = () => {
    if (stockLevel === 'critical') return theme.palette.error.main;
    if (stockLevel === 'low') return theme.palette.warning.main;
    return theme.palette.success.main;
  };
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: getColor(),
    display: 'inline-block',
    marginRight: 8,
  };
});

// Constants
const UNITS = [
  'kg', 'g', 'lb', 'oz',
  'L', 'ml', 'fl oz',
  'piece', 'dozen', 'pack', 'bottle', 'can', 'box',
  'plate', 'bowl', 'cup'
];

const ORDER_TYPES = [
  { value: 'dine_in', label: 'Dine In', icon: <Restaurant /> },
  { value: 'room_service', label: 'Room Service', icon: <RoomService /> },
  { value: 'vip_lounge', label: 'VIP Lounge', icon: <Store /> },
  { value: 'take_away', label: 'Take Away', icon: <TakeoutDining /> },
  { value: 'delivery', label: 'Delivery', icon: <DeliveryDining /> },
];

// API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getCurrentStaffId = () => {
  const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
  return storedProfile.staff_id || storedProfile.id || null;
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const HotelFBInventoryMgmt = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [logsOpen, setLogsOpen] = useState(false);
  // State management
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openStockDialog, setOpenStockDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [restockQuantity, setRestockQuantity] = useState(0);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);

  // Form states
  const [inventoryForm, setInventoryForm] = useState({
    hotel_code: getHotelCode(),
    item_name: '',
    category: '',
    unit: 'piece',
    min_stock: '',
    price_dine_in: '',
    price_room_service: '',
    price_vip_lounge: '',
    price_take_away: '',
    price_delivery: '',
    notes: '',
    is_active: true,
    created_by: getCurrentStaffId(),
  });

  const [categoryForm, setCategoryForm] = useState({
    hotel_code: getHotelCode(),
    category: '',
  });

  // API service functions
  const api = {
    getInventory: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching inventory:', error);
        throw new Error('Failed to fetch inventory');
      }
    },

    createInventoryItem: async (itemData) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/F&B/inventory.php`, itemData);
        return response.data;
      } catch (error) {
        console.error('Error creating inventory item:', error);
        throw new Error(error.response?.data?.error || 'Failed to create inventory item');
      }
    },

    updateInventoryItem: async (itemData) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/hotel/F&B/inventory.php`, itemData);
        return response.data;
      } catch (error) {
        console.error('Error updating inventory item:', error);
        throw new Error(error.response?.data?.error || 'Failed to update inventory item');
      }
    },

    deleteInventoryItem: async (itemId) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/hotel/F&B/inventory.php`, {
          data: { id: itemId, hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        throw new Error('Failed to delete inventory item');
      }
    },

    updateStock: async (itemId, quantityChange) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/F&B/stock_movement.php`, {
          id: itemId,
          hotel_code: getHotelCode(),
          quantity_change: quantityChange,
          updated_by: getCurrentStaffId()
        });
        return response.data;
      } catch (error) {
        console.error('Error updating stock:', error);
        throw new Error(error.response?.data?.error || 'Failed to update stock');
      }
    },

    getCategories: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory_categories.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching categories:', error);
        return { success: true, categories: [] };
      }
    },

    createCategory: async (categoryData) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/hotel/F&B/inventory_categories.php`, categoryData);
        return response.data;
      } catch (error) {
        console.error('Error creating category:', error);
        throw new Error('Failed to create category');
      }
    },

    deleteCategory: async (category) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/hotel/F&B/inventory_categories.php`, {
          data: { hotel_code: getHotelCode(), category }
        });
        return response.data;
      } catch (error) {
        console.error('Error deleting category:', error);
        throw new Error(error.response?.data?.error || 'Failed to delete category');
      }
    },

    getStatistics: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/F&B/statistics.php`, {
          params: { hotel_code: getHotelCode() }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching statistics:', error);
        return { success: true, statistics: {} };
      }
    },
  };

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadInventory(),
        loadCategories(),
        loadStatistics()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await api.getInventory();
      if (data.success) {
        setInventory(data.inventory || []);
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      if (data.success && data.categories) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await api.getStatistics();
      if (data.success && data.statistics) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  // Filtered data
  const filteredInventory = inventory.filter(item =>
    item.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedInventory = filteredInventory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stock level helper
  const getStockLevel = (currentStock, minStock) => {
    if (currentStock <= 0) return 'critical';
    if (currentStock <= minStock) return 'low';
    return 'good';
  };

  const getStockLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'error';
      case 'low': return 'warning';
      default: return 'success';
    }
  };

  // Dialog handlers
  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setInventoryForm({
        hotel_code: getHotelCode(),
        item_name: item.item_name || '',
        category: item.category || '',
        unit: item.unit || 'piece',
        min_stock: item.min_stock || '',
        price_dine_in: item.price_dine_in || '',
        price_room_service: item.price_room_service || '',
        price_vip_lounge: item.price_vip_lounge || '',
        price_take_away: item.price_take_away || '',
        price_delivery: item.price_delivery || '',
        notes: item.notes || '',
        is_active: item.is_active === 1,
        created_by: getCurrentStaffId(),
      });
    } else {
      setEditingItem(null);
      setInventoryForm({
        hotel_code: getHotelCode(),
        item_name: '',
        category: '',
        unit: 'piece',
        min_stock: '',
        price_dine_in: '',
        price_room_service: '',
        price_vip_lounge: '',
        price_take_away: '',
        price_delivery: '',
        notes: '',
        is_active: true,
        created_by: getCurrentStaffId(),
      });
    }
    setOpenDialog(true);
  };

  const handleOpenCategoryDialog = () => {
    setNewCategoryName('');
    setCategoryForm({
      hotel_code: getHotelCode(),
      category: '',
    });
    setOpenCategoryDialog(true);
  };

  const handleOpenStockDialog = (item) => {
    setSelectedItem(item);
    setRestockQuantity(0);
    setOpenStockDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setNewCategoryName('');
  };

  const handleCloseStockDialog = () => {
    setOpenStockDialog(false);
    setSelectedItem(null);
    setRestockQuantity(0);
  };

  // Form handlers
  const handleInventoryInputChange = (field) => (event) => {
    setInventoryForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCategorySelect = (event) => {
    const value = event.target.value;
    if (value === '__new__') {
      handleOpenCategoryDialog();
    } else {
      setInventoryForm(prev => ({
        ...prev,
        category: value
      }));
    }
  };

  // CRUD operations
  const handleSaveInventoryItem = async () => {
    if (!inventoryForm.item_name?.trim()) {
      showSnackbar('Please enter an item name', 'error');
      return;
    }
    
    if (!inventoryForm.category?.trim()) {
      showSnackbar('Please select/enter a category', 'error');
      return;
    }

    setUploading(true);
    try {
      let result;
      const submitData = {
        ...inventoryForm,
        min_stock: inventoryForm.min_stock ? parseFloat(inventoryForm.min_stock) : 0,
        price_dine_in: inventoryForm.price_dine_in ? parseFloat(inventoryForm.price_dine_in) : null,
        price_room_service: inventoryForm.price_room_service ? parseFloat(inventoryForm.price_room_service) : null,
        price_vip_lounge: inventoryForm.price_vip_lounge ? parseFloat(inventoryForm.price_vip_lounge) : null,
        price_take_away: inventoryForm.price_take_away ? parseFloat(inventoryForm.price_take_away) : null,
        price_delivery: inventoryForm.price_delivery ? parseFloat(inventoryForm.price_delivery) : null,
        is_active: inventoryForm.is_active ? 1 : 0,
      };

      if (editingItem) {
        submitData.id = editingItem.id;
        submitData.current_stock = editingItem.current_stock; // Keep existing stock
        submitData.updated_by = getCurrentStaffId();
        result = await api.updateInventoryItem(submitData);
      } else {
        submitData.current_stock = 0; // Start with 0 stock for new items
        result = await api.createInventoryItem(submitData);
      }

      if (result.success) {
        showSnackbar(editingItem ? 'Item updated!' : 'Item added!', 'success');
        await loadInventory();
        await loadCategories();
        handleCloseDialog();
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving inventory item:', error);
      showSnackbar(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCategory = async () => {
    const categoryName = newCategoryName.trim() || categoryForm.category?.trim();
    
    if (!categoryName) {
      showSnackbar('Please enter a category name', 'error');
      return;
    }

    setUploading(true);
    try {
      const result = await api.createCategory({ 
        hotel_code: getHotelCode(), 
        category: categoryName 
      });
      if (result.success) {
        showSnackbar('Category added successfully!', 'success');
        await loadCategories();
        // If this was triggered from the inventory form, set the category
        if (openDialog) {
          setInventoryForm(prev => ({ ...prev, category: categoryName }));
        }
        handleCloseCategoryDialog();
      }
    } catch (error) {
      showSnackbar('Failed to create category: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (window.confirm(`Are you sure you want to delete category "${category}"? This will not delete items in this category.`)) {
      try {
        const result = await api.deleteCategory(category);
        if (result.success) {
          showSnackbar('Category deleted!', 'success');
          await loadCategories();
        } else {
          showSnackbar(result.error || 'Failed to delete category', 'error');
        }
      } catch (error) {
        showSnackbar(error.message, 'error');
      }
    }
  };

  const handleDeleteInventoryItem = async (itemId, itemName) => {
    if (window.confirm(`Are you sure you want to delete "${itemName}"?`)) {
      try {
        const result = await api.deleteInventoryItem(itemId);
        if (result.success) {
          showSnackbar('Item deleted!', 'success');
          await loadInventory();
          await loadCategories();
        } else {
          showSnackbar(result.error || 'Failed to delete item', 'error');
        }
      } catch (error) {
        showSnackbar('Failed to delete item: ' + error.message, 'error');
      }
    }
  };

  const handleUpdateStock = async () => {
    if (!selectedItem) return;
    
    const quantityChange = parseFloat(restockQuantity);
    
    if (isNaN(quantityChange) || quantityChange <= 0) {
      showSnackbar('Please enter a valid positive quantity', 'error');
      return;
    }

    setUploading(true);
    try {
      const result = await api.updateStock(selectedItem.id, quantityChange);
      if (result.success) {
        showSnackbar(`Stock updated: ${selectedItem.item_name} now has ${result.new_stock} ${selectedItem.unit}`, 'success');
        await loadInventory();
        handleCloseStockDialog();
      } else {
        showSnackbar(result.error || 'Failed to update stock', 'error');
      }
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    loadData();
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Price formatter
  const formatPrice = (price) => {
    if (!price && price !== 0) return '—';
    return `₦${parseFloat(price).toFixed(2)}`;
  };

  // Tab content
  const renderInventoryTab = () => (
    <Box>
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Inventory Items ({inventory.length})</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="outlined" 
              startIcon={<Category />}
              onClick={handleOpenCategoryDialog}
            >
              Add Category
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()}
            >
              Add Item
            </Button>

            <Button 
  variant="outlined" 
  startIcon={<History />}
  onClick={() => setLogsOpen(true)}
>
  View Activity Log
</Button>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell><strong>Item Name</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="right"><strong>Stock</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell><strong>Min Stock</strong></TableCell>
                  <TableCell align="right"><strong>Dine In</strong></TableCell>
                  <TableCell align="right"><strong>Room Service</strong></TableCell>
                  <TableCell align="right"><strong>VIP Lounge</strong></TableCell>
                  <TableCell align="right"><strong>Take Away</strong></TableCell>
                  <TableCell align="right"><strong>Delivery</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInventory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No inventory items found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInventory.map((item) => {
                    const stockLevel = getStockLevel(item.current_stock, item.min_stock);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Fastfood color="action" />
                            <Typography fontWeight="medium">{item.item_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.category} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <StockIndicator stockLevel={stockLevel} />
                            <Typography 
                              color={stockLevel === 'critical' ? 'error.main' : stockLevel === 'low' ? 'warning.main' : 'text.primary'}
                              fontWeight={stockLevel !== 'good' ? 'bold' : 'normal'}
                            >
                              {item.current_stock}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell>{item.min_stock}</TableCell>
                        <TableCell align="right">{formatPrice(item.price_dine_in)}</TableCell>
                        <TableCell align="right">{formatPrice(item.price_room_service)}</TableCell>
                        <TableCell align="right">{formatPrice(item.price_vip_lounge)}</TableCell>
                        <TableCell align="right">{formatPrice(item.price_take_away)}</TableCell>
                        <TableCell align="right">{formatPrice(item.price_delivery)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={item.is_active ? 'Active' : 'Inactive'} 
                            size="small"
                            color={item.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Restock">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenStockDialog(item)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenDialog(item)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteInventoryItem(item.id, item.item_name)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredInventory.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      <FloatingActionButton color="primary" onClick={() => handleOpenDialog()}>
        <Add />
      </FloatingActionButton>
    </Box>
  );

  const renderAnalyticsTab = () => {
    const stats = statistics || {};
    const popularItems = stats.popular_items || [];
    const lowStockItems = stats.low_stock_items || 0;

    // Calculate category stats from inventory data
    const categoryMap = new Map();
    inventory.forEach(item => {
      if (item.category) {
        const existing = categoryMap.get(item.category) || { count: 0, totalStock: 0 };
        existing.count++;
        existing.totalStock += parseFloat(item.current_stock) || 0;
        categoryMap.set(item.category, existing);
      }
    });
    
    const categoryStats = Array.from(categoryMap.entries()).map(([category, data]) => ({
      category,
      count: data.count,
      total_stock: data.totalStock
    }));

    const totalStockValue = inventory.reduce((sum, item) => {
      const price = parseFloat(item.price_dine_in) || 0;
      const stock = parseFloat(item.current_stock) || 0;
      return sum + (price * stock);
    }, 0);

    const outOfStockCount = inventory.filter(item => (parseFloat(item.current_stock) || 0) <= 0).length;
    const lowStockCount = inventory.filter(item => {
      const stock = parseFloat(item.current_stock) || 0;
      const minStock = parseFloat(item.min_stock) || 0;
      return stock > 0 && stock <= minStock;
    }).length;

    return (
      <Box>
        <Typography variant="h4" gutterBottom color="primary" sx={{ mb: 3 }}>
          📊 Inventory Analytics Dashboard
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Total Items',
              value: inventory.length,
              icon: <Inventory />,
              color: 'primary',
              description: 'Active inventory items'
            },
            {
              title: 'Low Stock Items',
              value: lowStockCount,
              icon: <Warning />,
              color: 'warning',
              description: 'Items below minimum stock'
            },
            {
              title: 'Out of Stock',
              value: outOfStockCount,
              icon: <Warning />,
              color: 'error',
              description: 'Items with zero stock'
            },
           
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                        {stat.value}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {stat.description}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: `${stat.color}.light`, width: 50, height: 50 }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Category Distribution */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📁 Items by Category
                </Typography>
                {categoryStats.length > 0 ? (
                  <List>
                    {categoryStats.map((cat, index) => (
                      <ListItem key={index} divider={index < categoryStats.length - 1}>
                        <ListItemText
                          primary={cat.category}
                          secondary={`${cat.count} items • Total Stock: ${cat.total_stock}`}
                        />
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((cat.count / inventory.length) * 100, 100)} 
                          sx={{ width: 100, ml: 2 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No category data available
                  </Typography>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Popular Items (if available from API) */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  🔥 Popular Menu Items
                </Typography>
                {popularItems.length > 0 ? (
                  <List>
                    {popularItems.map((item, index) => (
                      <ListItem key={index} divider={index < popularItems.length - 1}>
                        <ListItemIcon>
                          <LocalOffer />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={`Quantity sold: ${item.total_quantity} • Revenue: ₦${parseFloat(item.total_revenue || 0).toFixed(2)}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No sales data available yet
                  </Typography>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Price Variations Report */}
          <Grid item xs={12}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  💰 Price Variations by Order Type
                </Typography>
                {inventory.filter(item => 
                  item.price_dine_in || item.price_room_service || item.price_vip_lounge || 
                  item.price_take_away || item.price_delivery
                ).length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Item</strong></TableCell>
                          <TableCell><strong>Category</strong></TableCell>
                          <TableCell align="right"><strong>Dine In</strong></TableCell>
                          <TableCell align="right"><strong>Room Service</strong></TableCell>
                          <TableCell align="right"><strong>VIP Lounge</strong></TableCell>
                          <TableCell align="right"><strong>Take Away</strong></TableCell>
                          <TableCell align="right"><strong>Delivery</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inventory.slice(0, 20).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.item_name}</TableCell>
                            <TableCell><Chip label={item.category} size="small" /></TableCell>
                            <TableCell align="right">{formatPrice(item.price_dine_in)}</TableCell>
                            <TableCell align="right">{formatPrice(item.price_room_service)}</TableCell>
                            <TableCell align="right">{formatPrice(item.price_vip_lounge)}</TableCell>
                            <TableCell align="right">{formatPrice(item.price_take_away)}</TableCell>
                            <TableCell align="right">{formatPrice(item.price_delivery)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No price variations found. Set different prices for order types to see variations.
                  </Typography>
                )}
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        {/* Refresh Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh Analytics
          </Button>
        </Box>
      </Box>
    );
  };

  const tabLabels = [
    { label: 'Inventory', icon: <Inventory /> },
    { label: 'Analytics', icon: <TrendingUp /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderInventoryTab();
      case 1:
        return renderAnalyticsTab();
      default:
        return renderInventoryTab();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            🍽️ Food & Beverage Inventory
          </Typography>
          <Typography variant="subtitle1">
            Manage inventory items, pricing, and stock levels
          </Typography>
        </Paper>

        <Paper elevation={1} sx={{ mt: 2, borderRadius: 2 }}>
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            {tabLabels.map((tab, index) => (
              <StyledTab 
                key={index} 
                label={
                  <Badge 
                    badgeContent={index === 0 ? inventory.length : 0} 
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                } 
                icon={tab.icon} 
                iconPosition="start" 
              />
            ))}
          </StyledTabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* Inventory Item Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Item Name *"
                value={inventoryForm.item_name}
                onChange={handleInventoryInputChange('item_name')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={inventoryForm.category}
                  onChange={handleCategorySelect}
                  label="Category *"
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                  <MenuItem value="__new__" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    + Add New Category...
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Current Stock - Display only when editing */}
            {editingItem && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Stock"
                  value={editingItem.current_stock}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Current stock level (read-only). Use Restock to update."
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Unit *</InputLabel>
                <Select
                  value={inventoryForm.unit}
                  onChange={handleInventoryInputChange('unit')}
                  label="Unit *"
                >
                  {UNITS.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Stock Alert Level"
                type="number"
                value={inventoryForm.min_stock}
                onChange={handleInventoryInputChange('min_stock')}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                helperText="Alert when stock falls below this level"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Pricing by Order Type
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dine In Price"
                type="number"
                value={inventoryForm.price_dine_in}
                onChange={handleInventoryInputChange('price_dine_in')}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Room Service Price"
                type="number"
                value={inventoryForm.price_room_service}
                onChange={handleInventoryInputChange('price_room_service')}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VIP Lounge Price"
                type="number"
                value={inventoryForm.price_vip_lounge}
                onChange={handleInventoryInputChange('price_vip_lounge')}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Take Away Price"
                type="number"
                value={inventoryForm.price_take_away}
                onChange={handleInventoryInputChange('price_take_away')}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Delivery Price"
                type="number"
                value={inventoryForm.price_delivery}
                onChange={handleInventoryInputChange('price_delivery')}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={inventoryForm.notes}
                onChange={handleInventoryInputChange('notes')}
                placeholder="Additional information about this item..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={inventoryForm.is_active}
                    onChange={(e) => setInventoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Item is active (available for ordering)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveInventoryItem}
            variant="contained"
            disabled={!inventoryForm.item_name || !inventoryForm.category || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : (editingItem ? 'Update Item' : 'Create Item')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={handleCloseCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add New Category
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name *"
                value={newCategoryName || categoryForm.category}
                onChange={(e) => {
                  setNewCategoryName(e.target.value);
                  setCategoryForm(prev => ({ ...prev, category: e.target.value }));
                }}
                required
                placeholder="e.g., Appetizers, Main Courses, Beverages, Desserts"
                helperText="Categories help organize your inventory items"
              />
            </Grid>
          </Grid>
          {categories.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Existing Categories:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categories.map(cat => (
                  <Chip 
                    key={cat} 
                    label={cat} 
                    onDelete={() => handleDeleteCategory(cat)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveCategory}
            variant="contained"
            disabled={(!newCategoryName && !categoryForm.category) || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Add Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restock Dialog - Only Add Stock */}
      <Dialog open={openStockDialog} onClose={handleCloseStockDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Restock: {selectedItem?.item_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Current Stock: <strong>{selectedItem?.current_stock} {selectedItem?.unit}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Minimum Stock Level: <strong>{selectedItem?.min_stock} {selectedItem?.unit}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quantity to Add"
                type="number"
                value={restockQuantity}
                onChange={(e) => setRestockQuantity(e.target.value)}
                InputProps={{ 
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">+</InputAdornment>
                }}
                helperText="Enter the amount to add to current stock"
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                After restock, new stock will be: <strong>{((parseFloat(selectedItem?.current_stock) || 0) + (parseFloat(restockQuantity) || 0)).toFixed(2)} {selectedItem?.unit}</strong>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStockDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateStock}
            variant="contained"
            color="success"
            startIcon={<ArrowUpward />}
            disabled={!restockQuantity || parseFloat(restockQuantity) <= 0 || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Add Stock'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

       <InventoryLogs 
        open={logsOpen} 
        onClose={() => setLogsOpen(false)} 
      />
    </Box>
  );
};

export default HotelFBInventoryMgmt;